/**
 * /iso-extractor orchestrator.
 *
 * Wires the disk-image cluster (Iso9660, Udf, El Torito, ExtractEngine, Hash,
 * Persistence, Handoff) into the blade DOM. State machine:
 *   idle → mounting → mounted → (extracting | hashing) → idle
 */
(function (w) {
    'use strict';
    var DI = w.__DiskImage;
    var I18N = w.IsoExtractorI18n || {};
    var ae = function (name, payload) {
        try { if (typeof w.__ae === 'function') w.__ae(name, payload || {}); } catch (_) {}
    };
    var toast = w.__toast || function (m) { console.log(m); };
    var handleErr = w.__handleError || function (e) { console.error(e); };
    var t = function (k, fallback) { return I18N[k] || fallback || k; };

    function _human(n) {
        if (n < 1024) return n + ' B';
        if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
        if (n < 1024 * 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + ' MB';
        return (n / 1024 / 1024 / 1024).toFixed(2) + ' GB';
    }

    function App(root) {
        this.root = root;
        this.state = 'idle';
        this.file = null;
        this.format = null;
        this.iso = null;
        this.udf = null;
        this.bootRec = null;
        this.engine = null;
        this.searchQuery = '';
        this.expanded = new Set();
        this._abort = null;
        this._readerForExtract = null;
        this._dom = {};
    }

    App.prototype.boot = function () {
        var self = this;
        self._cacheDom();
        self._wireUploader();
        self._wireActions();
        self._consumeHandoff();
    };

    App.prototype._cacheDom = function () {
        var r = this.root;
        this._dom.empty = r.querySelector('.iex-empty');
        this._dom.workspace = r.querySelector('.iex-workspace');
        this._dom.tree = r.querySelector('.iex-tree');
        this._dom.preview = r.querySelector('.iex-preview');
        this._dom.summary = r.querySelector('.iex-summary');
        this._dom.search = r.querySelector('.iex-search');
        this._dom.btnExtract = r.querySelector('.iex-btn-extract');
        this._dom.btnHash = r.querySelector('.iex-btn-hash');
        this._dom.btnReset = r.querySelector('.iex-btn-reset');
        this._dom.btnExportTree = r.querySelector('.iex-btn-export-tree');
        this._dom.btnInspect = r.querySelector('.iex-btn-inspect');
        this._dom.btnCompare = r.querySelector('.iex-btn-compare');
        this._dom.progress = r.querySelector('.iex-progress');
        this._dom.progressBar = r.querySelector('.iex-progress-bar');
        this._dom.progressText = r.querySelector('.iex-progress-text');
        this._dom.bootBlock = r.querySelector('.iex-boot');
    };

    App.prototype._wireUploader = function () {
        var self = this;
        var dropEl = this.root.querySelector('[data-uploader-id]') || document.getElementById('iexDrop');
        if (!dropEl) return;
        // Shared-uploader contract event. This tool shipped listening to
        // `uploader:filesready` (dispatched by nothing) + a raw input `change`
        // fallback that reads an already-cleared FileList — both dead, so no
        // file could enter the tool. See bin-cue-to-iso.js (bug 2026-07-07)
        // for the full account. Never wire the input directly.
        dropEl.addEventListener('tool-uploader:file', function (e) {
            var d = e.detail || {};
            var f = d.file || (d.files && d.files[0]);
            if (f) self.mount(f);
        });
    };

    App.prototype._wireActions = function () {
        var self = this;
        var d = this._dom;
        if (d.search) {
            d.search.addEventListener('input', function () {
                self.searchQuery = String(d.search.value || '').toLowerCase();
                self._renderTree();
            });
        }
        if (d.btnExtract) d.btnExtract.addEventListener('click', function () { self.extract(); });
        if (d.btnHash) d.btnHash.addEventListener('click', function () { self.hashWhole(); });
        if (d.btnReset) d.btnReset.addEventListener('click', function () { self.reset(); });
        if (d.btnExportTree) d.btnExportTree.addEventListener('click', function () { self.exportTree(); });
        if (d.btnInspect) d.btnInspect.addEventListener('click', function () { self.openInInspector(); });
        if (d.btnCompare) d.btnCompare.addEventListener('click', function () { self.compareWith(); });
        // Keyboard shortcuts on document
        document.addEventListener('keydown', function (ev) {
            if (self.state !== 'mounted') return;
            if (ev.target && /input|textarea|select/i.test(ev.target.tagName)) return;
            var kl = window.__keyLetter ? window.__keyLetter(ev) : (/^[a-z]$/i.test(ev.key || '') ? ev.key.toLowerCase() : '');
            var ks = window.__keySign ? window.__keySign(ev) : ev.key;
            if (ks === '/' && !(ev.ctrlKey || ev.metaKey || ev.altKey)) { ev.preventDefault(); if (d.search) d.search.focus(); return; }
            switch ((kl && (ev.ctrlKey || ev.metaKey || ev.altKey)) ? '' : (kl || ev.key)) {
                case 'e': self.extract(); break;
                case 'h': self.hashWhole(); break;
                case 'Escape': self._closePreview(); break;
            }
        });
    };

    App.prototype._consumeHandoff = function () {
        if (!DI || !DI.Handoff) return;
        var self = this;
        DI.Handoff.consume().then(function (rec) {
            if (rec && rec.file) self.mount(rec.file);
            else if (rec && rec.fileMeta) {
                toast(t('handoff_redrop', 'Re-drop ') + (rec.fileMeta.name || ''), 'info');
            }
        }).catch(function () {});
    };

    App.prototype.reset = function () {
        if (this._abort) { this._abort.abort(); this._abort = null; }
        this.iso = null;
        this.udf = null;
        this.bootRec = null;
        this.engine = null;
        this.format = null;
        this.file = null;
        this.state = 'idle';
        this.expanded.clear();
        if (this._dom.empty) this._dom.empty.style.display = '';
        if (this._dom.workspace) { this._dom.workspace.style.display = 'none'; this._dom.workspace.classList.remove('active'); }
    };

    App.prototype.mount = function (file) {
        var self = this;
        if (self.state === 'mounting' || self.state === 'extracting') return;
        self.state = 'mounting';
        self.file = file;
        self._showProgress(t('mounting', 'Mounting…'));
        ae('feature_use', { slug: 'iso-extractor', action: 'iso_mount_start', size_mb: Math.round(file.size / 1024 / 1024), file_ext: (file.name.split('.').pop() || '').toLowerCase() });

        var reader = new DI.BinaryReader(file);
        self._readerForExtract = reader;
        var t0 = Date.now();
        return DI.detect(file).then(function (detection) {
            self.format = detection.format;
            if (detection.format === 'iso9660' || detection.format === 'iso9660+udf') {
                return self._mountIso(reader).then(function () {
                    if (detection.format === 'iso9660+udf' && DI.Udf) {
                        return self._tryMountUdf(reader);
                    }
                });
            }
            if (detection.format === 'udf') {
                return self._tryMountUdf(reader);
            }
            throw (DI.errors.UnsupportedFilesystemError || function (m) { var e = new Error(m); e.name = 'UnsupportedFilesystemError'; return e; })('Format not supported by extractor: ' + detection.format, { detected: detection.format });
        }).then(function () {
            return self._mountBoot(reader);
        }).then(function () {
            self.state = 'mounted';
            self._hideProgress();
            self._renderMounted();
            ae('feature_use', {
                slug: 'iso-extractor',
                action: 'iso_mount_done',
                format: self.format,
                fs_layers: self._fsLayers(),
                file_count: self._allFiles().length,
                ms: Date.now() - t0
            });
        }).catch(function (err) {
            self.state = 'idle';
            self._hideProgress();
            handleErr(err, 'iso_mount');
            ae('feature_use', {
                slug: 'iso-extractor',
                action: 'iso_mount_failed',
                error_class: (err && err.name) || 'Error',
                msg: String(err && err.message || err).slice(0, 80)
            });
        });
    };

    App.prototype._mountIso = function (reader) {
        var self = this;
        self.iso = new DI.Iso9660(reader);
        return self.iso.mount().then(function () {
            if (DI.applyJoliet) return DI.applyJoliet(self.iso);
        }).then(function () {
            if (DI.applyRockRidge) return DI.applyRockRidge(self.iso);
        });
    };

    App.prototype._tryMountUdf = function (reader) {
        var self = this;
        if (!DI.Udf) return Promise.resolve();
        self.udf = new DI.Udf(reader);
        return self.udf.mount().catch(function (err) {
            self.udf = null;
            if (!self.iso) throw err;
        });
    };

    App.prototype._mountBoot = function (reader) {
        var self = this;
        if (!DI.parseBoot) return Promise.resolve();
        return DI.parseBoot(reader, self.iso ? self.iso.brLba : null).then(function (boot) {
            self.bootRec = boot;
        }).catch(function () {});
    };

    App.prototype._fsLayers = function () {
        var layers = [];
        if (this.iso) {
            layers.push('iso9660');
            if (this.iso.jolietApplied) layers.push('joliet');
            if (this.iso.rockRidgeApplied) layers.push('rock-ridge');
        }
        if (this.udf) layers.push('udf');
        return layers;
    };

    App.prototype._activeTreeRoot = function () {
        // Prefer UDF when present (more accurate for modern installer ISOs)
        if (this.udf && this.udf.root) return this.udf.root;
        if (this.iso && this.iso.root) return this.iso.root;
        return null;
    };

    App.prototype._allFiles = function () {
        var root = this._activeTreeRoot();
        if (!root) return [];
        var out = [];
        function walk(n) {
            if (!n) return;
            if (!n.isDir) out.push(n);
            if (n.children) n.children.forEach(walk);
        }
        walk(root);
        return out;
    };

    App.prototype._renderMounted = function () {
        if (this._dom.empty) this._dom.empty.style.display = 'none';
        // `.active` releases app.css's workspace-preview gate — without it the
        // shared [class$=-workspace]:not(.active) rule hides every child.
        if (this._dom.workspace) { this._dom.workspace.style.display = ''; this._dom.workspace.classList.add('active'); }
        this._renderSummary();
        this._renderBoot();
        this._renderTree();
    };

    App.prototype._renderSummary = function () {
        var d = this._dom.summary;
        if (!d) return;
        var files = this._allFiles();
        var totalBytes = files.reduce(function (s, f) { return s + (f.size || 0); }, 0);
        var pvd = (this.iso && this.iso.pvd) || (this.udf && this.udf.pvd) || {};
        var layers = this._fsLayers().join(', ') || 'unknown';
        d.innerHTML =
            '<div class="iex-summary-row"><b>' + t('vol_label', 'Volume') + ':</b> ' + (this._escape(pvd.volumeIdentifier || pvd.volumeId || '—')) + '</div>' +
            '<div class="iex-summary-row"><b>' + t('layers_label', 'Layers') + ':</b> ' + layers + '</div>' +
            '<div class="iex-summary-row"><b>' + t('files_label', 'Files') + ':</b> ' + files.length + ' (' + _human(totalBytes) + ')</div>';
    };

    App.prototype._renderBoot = function () {
        var d = this._dom.bootBlock;
        if (!d) return;
        if (!this.bootRec || !this.bootRec.bootable) {
            d.style.display = 'none';
            return;
        }
        d.style.display = '';
        var imgs = this.bootRec.bootImages || [];
        var html = '<h3>' + t('bootable', 'Bootable') + '</h3>';
        html += '<div>' + t('platforms', 'Platforms') + ': <b>' + (this.bootRec.platforms || []).join(', ') + '</b></div>';
        html += '<ul class="iex-boot-list">';
        imgs.forEach(function (b, i) {
            html += '<li>' + b.platform + ' / ' + b.mediaType + ' / LBA ' + b.imageLba + ' / ' + _human(b.imageSize) +
                    ' <button class="lg-btn lg-btn-sm" data-boot-idx="' + i + '">' + t('extract_boot', 'Extract boot image') + '</button></li>';
        });
        html += '</ul>';
        d.innerHTML = html;
        var self = this;
        d.querySelectorAll('button[data-boot-idx]').forEach(function (b) {
            b.addEventListener('click', function () {
                var idx = parseInt(b.getAttribute('data-boot-idx'), 10);
                self._extractBoot(self.bootRec.bootImages[idx]);
            });
        });
    };

    App.prototype._extractBoot = function (bootImage) {
        if (!DI.extractBootImage || !bootImage) return;
        var self = this;
        DI.extractBootImage(self._readerForExtract, bootImage).then(function (blob) {
            var url = URL.createObjectURL(blob);
            var a = document.createElement('a');
            a.href = url; a.download = 'boot-' + bootImage.platform + '-' + bootImage.mediaType + '.img';
            document.body.appendChild(a); a.click();
            setTimeout(function () { try { document.body.removeChild(a); } catch (_) {} URL.revokeObjectURL(url); }, 4000);
            ae('feature_use', { slug: 'iso-extractor', action: 'iso_boot_extracted', platform: bootImage.platform });
        }).catch(handleErr);
    };

    App.prototype._renderTree = function () {
        var d = this._dom.tree;
        if (!d) return;
        var root = this._activeTreeRoot();
        if (!root) { d.innerHTML = ''; return; }
        var query = this.searchQuery;
        var self = this;
        function nodeName(n) { return n.posixName || n.unicodeName || n.name; }
        function matches(n) {
            if (!query) return true;
            return nodeName(n).toLowerCase().indexOf(query) >= 0;
        }
        function render(n, depth) {
            if (!n) return '';
            if (n._parent === null && !depth) {
                var parts = [];
                (n.children || []).forEach(function (c) { parts.push(render(c, 1)); });
                return parts.join('');
            }
            var name = nodeName(n);
            var isDir = !!n.isDir;
            var mClass = matches(n) ? '' : ' iex-hidden';
            var icon = isDir ? '📁' : '📄';
            var size = isDir ? '' : _human(n.size || 0);
            var open = self.expanded.has(n);
            var html = '<div class="iex-node depth-' + depth + mClass + '" data-path="' + self._escape(n.path) + '">' +
                       '<span class="iex-toggle">' + (isDir ? (open ? '▾' : '▸') : '·') + '</span>' +
                       '<span class="iex-icon">' + icon + '</span>' +
                       '<span class="iex-name">' + self._escape(name) + '</span>' +
                       '<span class="iex-size">' + size + '</span>' +
                       '</div>';
            if (isDir && open && n.children) {
                n.children.forEach(function (c) { html += render(c, depth + 1); });
            }
            return html;
        }
        d.innerHTML = render(root, 0);
        // Wire clicks
        d.querySelectorAll('.iex-node').forEach(function (el) {
            el.addEventListener('click', function () {
                var path = el.getAttribute('data-path');
                var node = self._findByPath(path);
                if (!node) return;
                if (node.isDir) {
                    if (self.expanded.has(node)) self.expanded.delete(node); else self.expanded.add(node);
                    self._renderTree();
                } else {
                    self._previewNode(node);
                }
            });
        });
    };

    App.prototype._findByPath = function (path) {
        if (this.udf && this.udf.findByPath) {
            var u = this.udf.findByPath(path);
            if (u) return u;
        }
        if (this.iso && this.iso.findByPath) return this.iso.findByPath(path);
        return null;
    };

    App.prototype._previewNode = function (node) {
        var d = this._dom.preview;
        if (!d || !node || node.isDir) return;
        d.innerHTML = '<div class="iex-preview-meta"><b>' + this._escape(node.posixName || node.unicodeName || node.name) + '</b><br/>' +
                      this._escape(node.path) + ' · ' + _human(node.size) + ' · LBA ' + node.lba + '</div>';
        var self = this;
        var name = (node.name || '').toLowerCase();
        if (/\.(?:txt|md|log|cfg|conf|ini|json|xml|yaml|yml|js|ts|css|html|sh|py|rb|c|cpp|h)$/.test(name) && node.size <= 5 * 1024 * 1024) {
            var sz = Math.min(node.size, 1024 * 1024);
            this._readNode(node, 0, sz).then(function (b) {
                var s = new TextDecoder('utf-8', { fatal: false }).decode(b);
                var pre = document.createElement('pre');
                pre.className = 'iex-preview-text';
                pre.textContent = s;
                d.appendChild(pre);
            }).catch(function (e) { d.innerHTML += '<div class="iex-error">' + self._escape(String(e)) + '</div>'; });
        } else if (/\.(?:jpg|jpeg|png|webp|gif|bmp|svg)$/.test(name) && node.size <= 20 * 1024 * 1024) {
            this._readNode(node, 0, node.size).then(function (b) {
                var blob = new Blob([b]);
                var img = document.createElement('img');
                img.className = 'iex-preview-img';
                img.src = URL.createObjectURL(blob);
                img.onload = function () { setTimeout(function () { URL.revokeObjectURL(img.src); }, 5000); };
                d.appendChild(img);
            }).catch(function () {});
        }
        ae('feature_use', { slug: 'iso-extractor', action: 'iso_preview_opened', file_ext: name.split('.').pop() });
    };

    App.prototype._readNode = function (node, off, len) {
        if (this.iso && !node.udf) return this.iso.bytesOf(node, off, len);
        if (this.udf && node.udf) {
            // Read first len bytes via streamFile
            var stream = this.udf.streamFile(node);
            var reader = stream.getReader();
            var chunks = [];
            var got = 0;
            return reader.read().then(function pump(r) {
                if (r.done) return _join(chunks);
                if (got + r.value.length > len) {
                    chunks.push(r.value.subarray(0, len - got));
                    got = len;
                    return _join(chunks);
                }
                chunks.push(r.value);
                got += r.value.length;
                return reader.read().then(pump);
            });
        }
        return Promise.reject(new Error('cannot read'));
    };

    function _join(chunks) {
        var total = chunks.reduce(function (s, c) { return s + c.length; }, 0);
        var out = new Uint8Array(total);
        var off = 0;
        chunks.forEach(function (c) { out.set(c, off); off += c.length; });
        return out;
    }

    App.prototype._closePreview = function () {
        if (this._dom.preview) this._dom.preview.innerHTML = '';
    };

    App.prototype.extract = function () {
        var self = this;
        if (self.state !== 'mounted') return;
        self.state = 'extracting';
        var root = self._activeTreeRoot();
        var mountedFs = (self.udf && self.udf.root === root) ? self.udf : self.iso;
        var engine = new DI.ExtractEngine({
            tree: root,
            mountedFs: mountedFs,
            fs: self.format,
            onProgress: function (p) { self._renderExtractProgress(p); },
            onLog: function () {}
        });
        engine.selectAll();

        // Try FSA first
        var canFsa = ('showDirectoryPicker' in w);
        var pickP = canFsa ? w.showDirectoryPicker({ mode: 'readwrite' }) : Promise.resolve(null);
        var t0 = Date.now();
        pickP.then(function (dirHandle) {
            var mode = dirHandle ? 'fsa' : 'per-file';
            ae('feature_use', { slug: 'iso-extractor', action: 'iso_extract_start', mode: mode, file_count: engine._collectFiles().length });
            self._showProgress(t('extracting', 'Extracting…'));
            return engine.run({ mode: mode, dirHandle: dirHandle });
        }).then(function (result) {
            self.state = 'mounted';
            self._hideProgress();
            toast(t('extracted', 'Extracted') + ' ' + result.total + ' (' + _human(result.written) + ')', 'success');
            ae('feature_use', { slug: 'iso-extractor', action: 'iso_extract_done', mode: result.mode, file_count: result.total, total_bytes_mb: Math.round(result.written / 1024 / 1024), ms: Date.now() - t0, errors_count: result.errors.length });
        }).catch(function (err) {
            self.state = 'mounted';
            self._hideProgress();
            if (err && err.name === 'AbortError') {
                ae('feature_use', { slug: 'iso-extractor', action: 'iso_extract_cancelled' });
            } else {
                handleErr(err, 'iso_extract');
            }
        });
    };

    App.prototype.exportTree = function () {
        var self = this;
        var root = self._activeTreeRoot();
        if (!root) return;
        var files = self._allFiles();
        var fmt = (prompt(t('export_format_prompt', 'Format: txt / json / csv / m3u'), 'txt') || 'txt').toLowerCase().trim();
        var content, mime, ext;
        if (fmt === 'json') {
            var nodes = [];
            function walk(n) {
                if (n._parent !== null) {
                    nodes.push({
                        path: n.path, name: n.posixName || n.unicodeName || n.name,
                        isDir: n.isDir, size: n.size, lba: n.lba,
                        symlink: n.symlink || null, mode: n.mode || null
                    });
                }
                if (n.children) n.children.forEach(walk);
            }
            walk(root);
            content = JSON.stringify({ volumeId: (self.iso && self.iso.pvd && self.iso.pvd.volumeId) || '', fsLayers: self._fsLayers(), nodes: nodes }, null, 2);
            mime = 'application/json'; ext = 'json';
        } else if (fmt === 'csv') {
            var lines = ['path,name,is_dir,size_bytes,lba'];
            function walkCsv(n) {
                if (n._parent !== null) {
                    var nm = (n.posixName || n.unicodeName || n.name || '').replace(/"/g, '""');
                    lines.push('"' + n.path.replace(/"/g, '""') + '","' + nm + '",' + n.isDir + ',' + (n.size || 0) + ',' + n.lba);
                }
                if (n.children) n.children.forEach(walkCsv);
            }
            walkCsv(root);
            content = lines.join('\n');
            mime = 'text/csv'; ext = 'csv';
        } else if (fmt === 'm3u') {
            var media = files.filter(function (n) { return /\.(?:mp3|m4a|aac|flac|ogg|opus|wav|wma|wv|ape|mpc|mp4|m4v|mkv|webm|mov|avi|wmv)$/i.test(n.name || ''); });
            var m3u = ['#EXTM3U'];
            media.forEach(function (n) {
                m3u.push('#EXTINF:-1,' + (n.posixName || n.unicodeName || n.name));
                m3u.push(n.path);
            });
            content = m3u.join('\n');
            mime = 'audio/x-mpegurl'; ext = 'm3u';
        } else {
            var indent = function (n) {
                var depth = 0;
                var p = n;
                while (p && p._parent) { depth++; p = p._parent; }
                return new Array(depth).join('  ');
            };
            var tlines = ['# ' + ((self.iso && self.iso.pvd && self.iso.pvd.volumeId) || 'image') + ' (' + self._fsLayers().join('+') + ')'];
            function tw(n) {
                if (n._parent !== null) {
                    var nm = n.posixName || n.unicodeName || n.name;
                    var sz = n.isDir ? '' : ('  (' + n.size + ' B)');
                    tlines.push(indent(n) + (n.isDir ? '[' : '') + nm + (n.isDir ? ']' : '') + sz);
                }
                if (n.children) n.children.forEach(tw);
            }
            tw(root);
            content = tlines.join('\n');
            mime = 'text/plain'; ext = 'txt';
        }
        var blob = new Blob([content], { type: mime });
        var fname = ((self.iso && self.iso.pvd && self.iso.pvd.volumeId) || 'image').toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-tree.' + ext;
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url; a.download = fname;
        document.body.appendChild(a); a.click();
        setTimeout(function () { try { document.body.removeChild(a); } catch (_) {} URL.revokeObjectURL(url); }, 4000);
        ae('feature_use', { slug: 'iso-extractor', action: 'iso_tree_exported', format: ext, file_count: files.length });
    };

    App.prototype.compareWith = function () {
        var self = this;
        if (self.state !== 'mounted') { toast(t('mount_first', 'Mount an ISO first'), 'warning'); return; }
        var input = document.createElement('input');
        input.type = 'file';
        input.accept = '.iso,.udf,.img';
        input.style.display = 'none';
        document.body.appendChild(input);
        input.addEventListener('change', function () {
            var f2 = input.files && input.files[0];
            if (input.parentNode) input.parentNode.removeChild(input);
            if (!f2) return;
            self._runCompare(f2);
        });
        input.click();
    };

    App.prototype._runCompare = function (file2) {
        var self = this;
        if (!self.iso) return;
        self._showProgress(t('comparing', 'Comparing…'));
        var t0 = Date.now();
        ae('feature_use', { slug: 'iso-extractor', action: 'iso_compare_start', file_size_mb: Math.round(file2.size / 1024 / 1024) });
        var reader2 = new DI.BinaryReader(file2);
        var iso2 = new DI.Iso9660(reader2);
        var udf2 = null;
        return iso2.mount().then(function () {
            if (DI.applyJoliet) return DI.applyJoliet(iso2);
        }).then(function () {
            if (DI.applyRockRidge) return DI.applyRockRidge(iso2);
        }).then(function () {
            return DI.detect(file2);
        }).then(function (det2) {
            if ((det2.format === 'udf' || det2.format === 'iso9660+udf') && DI.Udf) {
                udf2 = new DI.Udf(reader2);
                return udf2.mount().catch(function () { udf2 = null; });
            }
        }).then(function () {
            // Build path → {size, isDir} maps for both trees
            var rootA = self._activeTreeRoot();
            var rootB = (udf2 && udf2.root) || iso2.root;
            var mapA = _flatten(rootA);
            var mapB = _flatten(rootB);
            var diff = _diffMaps(mapA, mapB);
            self._hideProgress();
            self._renderCompareResult(diff, file2.name);
            ae('feature_use', {
                slug: 'iso-extractor', action: 'iso_compare_done',
                added: diff.added.length, removed: diff.removed.length, changed: diff.changed.length, identical: diff.identical,
                ms: Date.now() - t0
            });
        }).catch(function (err) {
            self._hideProgress();
            handleErr(err, 'iso_compare');
        });
    };

    function _flatten(root) {
        var m = new Map();
        function walk(n) {
            if (n._parent !== null) m.set(n.path, { size: n.size, isDir: n.isDir });
            if (n.children) n.children.forEach(walk);
        }
        if (root) walk(root);
        return m;
    }
    function _diffMaps(a, b) {
        var added = [], removed = [], changed = [];
        var identical = 0;
        a.forEach(function (va, p) {
            var vb = b.get(p);
            if (!vb) { removed.push({ path: p, size: va.size, isDir: va.isDir }); return; }
            if (va.size !== vb.size || va.isDir !== vb.isDir) {
                changed.push({ path: p, sizeA: va.size, sizeB: vb.size, isDir: va.isDir });
            } else {
                identical++;
            }
        });
        b.forEach(function (vb, p) {
            if (!a.has(p)) added.push({ path: p, size: vb.size, isDir: vb.isDir });
        });
        return { added: added, removed: removed, changed: changed, identical: identical };
    }

    App.prototype._renderCompareResult = function (diff, name2) {
        var self = this;
        var d = self._dom.preview;
        if (!d) return;
        var html = '<div class="iex-compare">' +
            '<h3 style="margin:0 0 8px">' + t('compare_title', 'Compare') + ': ' + self._escape(name2) + '</h3>' +
            '<div style="display:flex;gap:12px;flex-wrap:wrap;font-size:12px;margin-bottom:8px">' +
            '<span style="color:#10b981">' + t('compare_added', 'Added') + ': ' + diff.added.length + '</span>' +
            '<span style="color:#ef4444">' + t('compare_removed', 'Removed') + ': ' + diff.removed.length + '</span>' +
            '<span style="color:#f59e0b">' + t('compare_changed', 'Changed') + ': ' + diff.changed.length + '</span>' +
            '<span style="color:var(--lg-text-muted)">' + t('compare_identical', 'Identical') + ': ' + diff.identical + '</span>' +
            '</div>';
        function rows(items, color) {
            return items.slice(0, 200).map(function (i) {
                var detail = i.sizeA != null ? (i.sizeA + ' → ' + i.sizeB + ' B') : (i.size + ' B' + (i.isDir ? ' [dir]' : ''));
                return '<div style="font-size:12px;padding:2px 0;color:' + color + ';font-family:ui-monospace,monospace">' +
                    self._escape(i.path) + '  <span style="color:var(--lg-text-muted)">' + detail + '</span>' +
                    '</div>';
            }).join('');
        }
        if (diff.added.length) html += '<details open><summary style="color:#10b981;cursor:pointer">+' + diff.added.length + '</summary>' + rows(diff.added, '#10b981') + '</details>';
        if (diff.removed.length) html += '<details open><summary style="color:#ef4444;cursor:pointer">−' + diff.removed.length + '</summary>' + rows(diff.removed, '#ef4444') + '</details>';
        if (diff.changed.length) html += '<details open><summary style="color:#f59e0b;cursor:pointer">~' + diff.changed.length + '</summary>' + rows(diff.changed, '#f59e0b') + '</details>';
        html += '<button class="lg-btn lg-btn-sm iex-cmp-export" style="margin-top:8px">' + t('compare_export', 'Export diff (JSON)') + '</button>';
        html += '</div>';
        d.innerHTML = html;
        d.querySelectorAll('.iex-cmp-export').forEach(function (b) {
            b.addEventListener('click', function () {
                var blob = new Blob([JSON.stringify(diff, null, 2)], { type: 'application/json' });
                var url = URL.createObjectURL(blob);
                var a = document.createElement('a');
                a.href = url; a.download = 'iso-compare.json';
                document.body.appendChild(a); a.click();
                setTimeout(function () { try { document.body.removeChild(a); } catch (_) {} URL.revokeObjectURL(url); }, 4000);
                ae('feature_use', { slug: 'iso-extractor', action: 'iso_compare_exported' });
            });
        });
    };

    App.prototype.openInInspector = function () {
        var self = this;
        if (!self.file || !DI.Handoff) return;
        ae('feature_use', { slug: 'iso-extractor', action: 'iso_open_in_sibling', target: 'disk-image-inspector' });
        DI.Handoff.send('disk-image-inspector', { file: self.file, sourceSlug: 'iso-extractor' });
    };

    App.prototype.hashWhole = function () {
        var self = this;
        if (!self.file) return;
        if (!DI.Hash) return;
        self._showProgress(t('hashing', 'Hashing…'));
        var t0 = Date.now();
        ae('feature_use', { slug: 'iso-extractor', action: 'iso_hash_start', algo: 'sha-256', size_mb: Math.round(self.file.size / 1024 / 1024) });
        DI.Hash.hash(self.file, 'sha-256', function (p) {
            self._showProgress((p.processed && p.total) ? Math.round(100 * p.processed / p.total) + '%' : '—');
        }).then(function (hex) {
            self._hideProgress();
            toast('SHA-256: ' + hex.slice(0, 16) + '…', 'success');
            try { navigator.clipboard.writeText(hex); } catch (_) {}
            ae('feature_use', { slug: 'iso-extractor', action: 'iso_hash_done', algo: 'sha-256', ms: Date.now() - t0 });
        }).catch(function (err) {
            self._hideProgress();
            handleErr(err, 'iso_hash');
        });
    };

    App.prototype._renderExtractProgress = function (p) {
        if (this._dom.progressBar) this._dom.progressBar.style.width = (p.total ? Math.round(100 * p.current / p.total) : 0) + '%';
        if (this._dom.progressText) {
            var txt = (p.current || 0) + ' / ' + (p.total || 0) + ' · ' + _human(p.bytesWritten || 0);
            this._dom.progressText.textContent = txt;
        }
    };
    App.prototype._showProgress = function (txt) {
        if (this._dom.progress) this._dom.progress.style.display = '';
        if (this._dom.progressText) this._dom.progressText.textContent = txt || '';
    };
    App.prototype._hideProgress = function () {
        if (this._dom.progress) this._dom.progress.style.display = 'none';
    };

    App.prototype._escape = function (s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    };

    w.__IsoExtractor = { App: App };

    document.addEventListener('DOMContentLoaded', function () {
        var root = document.querySelector('.iex-root');
        if (!root) return;
        var app = new App(root);
        app.boot();
        w.__IsoExtractorApp = app;
        try { if (w.Timbrica3D && w.Timbrica3D.init) w.Timbrica3D.init('iso-extractor'); } catch (_) {}
    });
})(window);
